import { type FC, useEffect, useState } from 'react';
import { useToast, TextLink, Text } from 'braid-design-system';
import { useTranslations } from '@vocab/react';
import { useQueryParam, StringParam } from 'use-query-params';

import type { IStore } from '../../stores/Store';
import {
  CandidateManagementApp,
  type CandidateManagementAppProps,
} from '../../App/AppPageBlock';
import {
  navigateToAccountSelectionPage,
  navigateToJobListPage,
  navigateToSignInPageWithReturnUrl,
} from './utils/utils';
import fetchInitialJobApplications from './fetchInitialJobApplications';
import useQueryParamsStore from '../../utils/hooks/useQueryParamsStore';

import { logError } from 'src/utils/logError';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from './.vocab';
import { getAdvertiserIds, isValidId } from 'src/utils/advertiserIds';
import { useStores } from 'src/utils/hooks/useStores';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from './queryParams';
import authServiceFactory from 'src/services/authServiceFactory';
import { ErrorContent } from 'src/App/ActionsPage/ErrorScreen/ErrorContent';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { CandidateManagementAppSkeleton } from 'src/App/AppPageBlock/CandidateManagementAppSkeleton';
import type { Language } from '@seek/melways-sites';
import { AccessType } from 'src/stores/JobStore/types';
import { useExperimentConfig } from 'src/App/CandidateManagementPage/ExperimentContext';

type TranslateFn = ReturnType<typeof useTranslations>['t'];

export interface HandleEasyAccessTranslations {
  'application has been moved': ({
    firstName,
    bucket,
  }: {
    firstName: string;
    bucket: string;
  }) => string;
  close: () => string;
}

const handleEasyAccessMessages = (
  t: TranslateFn,
): HandleEasyAccessTranslations => ({
  'application has been moved': ({ firstName, bucket }) =>
    t('application has been moved', { firstName, bucket }) as string,
  close: () => t('close') as string,
});

interface Props {
  employerHeader: JSX.Element;
}
export type JobIdType = 'jobId' | 'positionId';

interface AccessAndGetJobProps {
  store: IStore;
  jobIdType: JobIdType;
  expectedJobId: number; // either jobId or PositionId
  language: Language;
  selectedProspectId?: string;
}

const checkAccessAndFetchJobInformation = async ({
  store,
  jobIdType,
  expectedJobId,
  language,
}: AccessAndGetJobProps) => {
  const jobInfo = await store.jobStore.loadHirerJobDetailsAndAccess(
    jobIdType,
    String(expectedJobId),
    language,
  );

  if (store.jobStore.accessType === AccessType.INCORRECT_ADVERTISER) {
    navigateToAccountSelectionPage();
  }

  if (!jobInfo) {
    return { success: false } as const;
  }

  return { success: true, jobInfo } as const;
};

const CandidateManagementPage: FC<Props> = ({ employerHeader }) => {
  const store = useStores();
  const [query, setQuery] = useQueryParamsStore();
  const showToast = useToast();
  const { getLabelFromBucket } = useBucketLabel();
  const { t } = useTranslations(translations);
  const messages = handleEasyAccessMessages(t);
  const [hasLoginRedirectError, setHasLoginRedirectError] = useState(false);
  const { language, site } = useAppConfig();
  const [initialLoadResponse, setInitialLoadResponse] = useState<
    CandidateManagementAppProps['initialLoadResponse'] | undefined
  >();
  const { isExperimentEnabled } = useExperimentConfig();

  const [tab, setTabQueryParam] = useQueryParam(
    CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
    StringParam,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getTrackingExperiments = ():
    | Array<{
        num: number;
        id: string;
        variant: string;
      }>
    | undefined => {
    const experiments = [];
    if (isExperimentEnabled('growth-placement-passive-flow')) {
      experiments.push({
        num: 3,
        id: 'cm_growth_placement',
        variant: 'passive',
      });
    }
    if (isExperimentEnabled('growth-placement-assertive-flow-10-days')) {
      experiments.push({
        num: 3,
        id: 'cm_growth_placement',
        variant: 'assertive-10-days',
      });
    }
    if (isExperimentEnabled('growth-placement-assertive-flow-25-days')) {
      experiments.push({
        num: 3,
        id: 'cm_growth_placement',
        variant: 'assertive-25-days',
      });
    }

    if (experiments.length > 0) {
      return experiments;
    }

    return undefined;
  };

  useEffect(() => {
    if (!initialLoadResponse) {
      const { advertiserId, seekAdvertiserId } = getAdvertiserIds();

      if (!isValidId(advertiserId) || !isValidId(seekAdvertiserId)) {
        const authService = authServiceFactory();

        authService
          .loginWithRedirect()
          .then(() => {})
          .catch((error) => {
            logError(
              {
                errorCode: 'LOGIN_WITH_REDIRECTION_URL',
                description: 'Error attempting: loginWithRedirect',
              },
              {
                error,
              },
            );
            setHasLoginRedirectError(true);
          });
        return;
      }

      const {
        positionId: queryPositionId,
        jobId: queryJobId,
        selectedProspectId,
      } = query;
      let accessAndGetJobProps: AccessAndGetJobProps;

      if (queryPositionId) {
        accessAndGetJobProps = {
          store,
          jobIdType: 'positionId',
          expectedJobId: queryPositionId,
          language,
          selectedProspectId,
        };
      } else if (queryJobId) {
        accessAndGetJobProps = {
          store,
          jobIdType: 'jobId',
          expectedJobId: queryJobId,
          language,
          selectedProspectId,
        };
      } else {
        return navigateToJobListPage();
      }

      checkAccessAndFetchJobInformation(accessAndGetJobProps)
        .then((result) => {
          if (result.success) {
            const positionId = store.jobStore.job.jobInformation.positionId;
            const jobId = store.jobStore.job.jobInformation.jobId!;
            const jobInfo = result.jobInfo;
            const experiments = getTrackingExperiments();

            fetchInitialJobApplications(
              store,
              (page: number | undefined) => setQuery.updatePage(page),
              advertiserId!,
              seekAdvertiserId!,
              positionId,
              jobId,
              query,
              showToast,
              getLabelFromBucket,
              messages,
              language,
              site,
              experiments,
            )
              .then(() => {
                setInitialLoadResponse({
                  ok: true,
                  jobId,
                  canBeUpgraded: jobInfo.canBeUpgraded,
                  canShowHomeLocationFilterInCandidateManagement:
                    jobInfo.canShowHomeLocationFilterInCandidateManagement,
                  canShowNationalityFilterInCandidateManagement:
                    jobInfo.canShowNationalityFilterInCandidateManagement,
                  canShowJobPlacement:
                    jobInfo.placement.__typename ===
                      'JobPlacementResponseSuccess' &&
                    jobInfo.placement.result.shouldShowPlacementDialog,
                });
              })
              .catch((error) => {
                if (error.message === 'UNAUTHENTICATED') {
                  navigateToSignInPageWithReturnUrl();
                  setInitialLoadResponse({ ok: false });
                  return;
                }
                logError(
                  {
                    errorCode: 'FETCH_INITIAL_JOB_APPLICATIONS',
                    description:
                      'Error attempting: fetchInitialJobApplications',
                  },
                  {
                    error,
                  },
                );
                setInitialLoadResponse({ ok: false });
              });
          } else {
            setInitialLoadResponse({ ok: false });
          }
        })
        .catch((error) => {
          logError(
            {
              errorCode: 'FETCH_ACCESS_AND_JOB_INFORMATION',
              description: 'Error attempting: fetchInitialJobApplications',
            },
            {
              error,
            },
          );
          store.jobStore.setIsError();
          setInitialLoadResponse({ ok: false });
        });
    }
  }, [
    query.positionId,
    query.bucket,
    query.page,
    query.selectedProspectId,
    store,
    query.search,
    query,
    setQuery,
    showToast,
    getLabelFromBucket,
    messages,
    setTabQueryParam,
    tab,
    language,
    site,
    initialLoadResponse,
    getTrackingExperiments,
  ]);

  useEffect(() => {
    if (query.referrer === 'edit-page-success') {
      showToast({
        message: t('Your job has been updated'),
        tone: 'positive',
        closeLabel: t('close'),
      });
      setQuery.clearReferrer();
    }
  }, [showToast, query.referrer, setQuery, t]);

  if (hasLoginRedirectError) {
    return (
      <ErrorContent
        errorCode="500"
        heading={t('Can we try that again?')}
        body={
          <Text tone="secondary">
            {t("Retry the page to see your candidate's profile", {
              Link: (children) => (
                <TextLink href={window.location.href}>{children}</TextLink>
              ),
            })}
          </Text>
        }
      />
    );
  }

  if (!initialLoadResponse) {
    return <CandidateManagementAppSkeleton />;
  }

  return (
    <CandidateManagementApp
      employerHeader={employerHeader}
      initialLoadResponse={initialLoadResponse}
    />
  );
};

export default CandidateManagementPage;
